/**
 * The photoreal lens, shown in the live 3D view (the Photoreal window's
 * Preview): a focal length in mm, full-frame — 36 mm across the picture's
 * longer side, as the renderer takes it (scripts/blender/render_room.py
 * apply_lens). The viewer draws with that lens's field of view while it is
 * set, and goes back to the user's own zoom when it is not; and it reports its
 * own field of view, so the window can say what "View" is in millimetres.
 */

let previewMm: number | null = null;
let view: { fov: number; aspect: number } | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** The lens to show in the 3D view (mm), or null: the view's own. */
export function setLensPreview(mm: number | null): void {
  if (previewMm === mm) return;
  previewMm = mm;
  notify();
}

export function getLensPreview(): number | null {
  return previewMm;
}

/** The 3D view's own camera: its vertical field of view (degrees) and aspect. */
export function reportViewLens(fov: number, aspect: number): void {
  if (view && Math.abs(view.fov - fov) < 0.01 && Math.abs(view.aspect - aspect) < 0.001) return;
  view = { fov, aspect };
  notify();
}

export function getViewLens(): { fov: number; aspect: number } | null {
  return view;
}

export function onLensChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

const RAD = Math.PI / 180;

/** The vertical field of view (degrees) of a lens of `mm` on a full-frame sensor across the longer side. */
export function verticalFovForMm(mm: number, aspect: number): number {
  const half = Math.atan(18 / mm); // half the field across the 36 mm side
  if (aspect >= 1) return (2 * Math.atan(Math.tan(half) / aspect)) / RAD;
  return (2 * half) / RAD;
}

/** The focal length (mm, full-frame across the longer side) a view of `fov` (vertical, degrees) at `aspect` is. */
export function mmForView(fov: number, aspect: number): number {
  const halfV = (fov * RAD) / 2;
  const halfLong = aspect >= 1 ? Math.atan(Math.tan(halfV) * aspect) : halfV;
  return 18 / Math.tan(halfLong);
}
